import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Sparkles, Bot, Brain, Mic, Globe, Phone, Loader2, Play, Pause,
  ChevronRight, ChevronLeft, Check, Settings, Search, Volume2,
  Shield, Target, MessageSquare, Info, Copy, Wrench,
  Code, Link, Zap, FileText, SlidersHorizontal, GitBranch
} from 'lucide-react';
import { AgentConfigShell, type AgentSectionDef } from '@/components/ai/shell/AgentConfigShell';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiClient, getPublicBaseUrl, type ApiResponse } from '@/lib/api';
import { isTenantLimitHandled } from '@/lib/tenantLimits';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { KnowledgeBaseManager } from '@/components/ai/KnowledgeBaseManager';
import VoiceAgentFlowBuilder, { type FlowDefinition } from '@/components/ai/VoiceAgentFlowBuilder';
import PreCallHooksSection, { normalizePreCallHooks, type PreCallHooksConfig } from '@/components/ai/PreCallHooksSection';
import {
  KbModelChangeGuardDialog,
  useKbModelChangeGuard,
  type KbGuardedPerformSaveArgs,
} from '@/components/ai/KbModelChangeGuardDialog';
import { getErrorMessage } from '../../lib/utils';


export interface VadConfig {
  sip_start_sensitivity: string;
  sip_end_sensitivity: string;
  sip_prefix_padding_ms: number;
  sip_silence_duration_ms: number;
  webrtc_start_sensitivity: string;
  webrtc_end_sensitivity: string;
  webrtc_prefix_padding_ms: number;
  webrtc_silence_duration_ms: number;
}

export interface WebrtcWidgetConfig {
  color: string;
  position: string;
  cta_text: string;
  allowed_domains: string[];
}

export interface GeminiVoiceAgentItem {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  model: string;
  voice: string;
  language: string;
  system_prompt?: string;
  first_message?: string;
  temperature: number;
  max_duration_seconds: number;
  channel_sip: boolean;
  channel_webrtc: boolean;
  webrtc_widget_config: WebrtcWidgetConfig;
  rag_enabled: boolean;
  rag_synthesis_mode: string;
  tools_config: Record<string, unknown>[];
  google_search_enabled: boolean;
  code_execution_enabled: boolean;
  affective_dialog: boolean;
  proactive_audio: boolean;
  pre_call_hooks: PreCallHooksConfig | string[];
  transcription_enabled: boolean;
  sentiment_enabled: boolean;
  context_memory_enabled: boolean;
  semantic_memory_enabled: boolean;
  is_active: boolean;
  metadata: Record<string, unknown>;
  fallback_destination_type?: string;
  fallback_destination_id?: string;
  tool_scheduling?: string;
  vad_config?: VadConfig;
  flow_definition?: FlowDefinition | null;
  created_at: string;
  updated_at: string;
}

interface GeminiVoiceAgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent?: GeminiVoiceAgentItem | null;
  onSaved: () => void;
}

const GEMINI_VOICES = [
  { id: 'Puck', name: 'Puck', gender: 'Maschile', style: 'Giovane, energico' },
  { id: 'Charon', name: 'Charon', gender: 'Maschile', style: 'Profondo, autorevole' },
  { id: 'Kore', name: 'Kore', gender: 'Femminile', style: 'Calmo, professionale' },
  { id: 'Fenrir', name: 'Fenrir', gender: 'Maschile', style: 'Deciso, chiaro' },
  { id: 'Aoede', name: 'Aoede', gender: 'Femminile', style: 'Caldo, musicale' },
  { id: 'Leda', name: 'Leda', gender: 'Femminile', style: 'Naturale, accogliente' },
  { id: 'Orus', name: 'Orus', gender: 'Maschile', style: 'Maturo, rassicurante' },
  { id: 'Zephyr', name: 'Zephyr', gender: 'Neutro', style: 'Leggero, fresco' },
  { id: 'Achernar', name: 'Achernar', gender: 'Femminile', style: 'Cristallino, elegante' },
  { id: 'Achird', name: 'Achird', gender: 'Maschile', style: 'Amichevole, conversazionale' },
  { id: 'Algenib', name: 'Algenib', gender: 'Maschile', style: 'Forte, chiaro' },
  { id: 'Algieba', name: 'Algieba', gender: 'Maschile', style: 'Sicuro, diretto' },
  { id: 'Alnilam', name: 'Alnilam', gender: 'Maschile', style: 'Profondo, narrativo' },
  { id: 'Autonoe', name: 'Autonoe', gender: 'Femminile', style: 'Vivace, espressivo' },
  { id: 'Callirrhoe', name: 'Callirrhoe', gender: 'Femminile', style: 'Dolce, armonioso' },
  { id: 'Despina', name: 'Despina', gender: 'Femminile', style: 'Allegro, brillante' },
  { id: 'Enceladus', name: 'Enceladus', gender: 'Maschile', style: 'Solenne, robusto' },
  { id: 'Erinome', name: 'Erinome', gender: 'Femminile', style: 'Delicato, gentile' },
  { id: 'Gacrux', name: 'Gacrux', gender: 'Maschile', style: 'Maturo, stabile' },
  { id: 'Iapetus', name: 'Iapetus', gender: 'Maschile', style: 'Chiaro, articolato' },
  { id: 'Laomedeia', name: 'Laomedeia', gender: 'Femminile', style: 'Rilassato, sereno' },
  { id: 'Pulcherrima', name: 'Pulcherrima', gender: 'Femminile', style: 'Raffinato, preciso' },
  { id: 'Rasalgethi', name: 'Rasalgethi', gender: 'Maschile', style: 'Autorevole, caldo' },
  { id: 'Sulafat', name: 'Sulafat', gender: 'Femminile', style: 'Luminoso, coinvolgente' },
];

const FALLBACK_GEMINI_MODELS = [
  { id: 'gemini-2.5-flash-native-audio-latest', name: 'Gemini 2.5 Flash Native Audio (Latest)', desc: 'Ultima versione stabile, audio nativo bidirezionale' },
];

const LANGUAGES = [
  { code: 'it', name: 'Italiano' }, { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' }, { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' }, { code: 'pt', name: 'Português' },
  { code: 'nl', name: 'Nederlands' }, { code: 'pl', name: 'Polski' },
  { code: 'ru', name: 'Русский' }, { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' }, { code: 'zh', name: '中文' },
  { code: 'ar', name: 'العربية' }, { code: 'hi', name: 'हिन्दी' },
  { code: 'tr', name: 'Türkçe' }, { code: 'ro', name: 'Română' },
];

const PROMPT_TEMPLATES = [
  {
    id: 'receptionist',
    name: 'Receptionist',
    icon: '📞',
    prompt: `Sei un receptionist virtuale professionale per {{azienda}}. Il tuo compito è accogliere i chiamanti con cortesia, comprendere le loro esigenze e indirizzarli al reparto o alla persona corretta.

Regole:
- Rispondi sempre in modo professionale e cortese
- Chiedi il nome del chiamante e il motivo della chiamata
- Se non riesci a soddisfare la richiesta, offri di trasferire la chiamata
- Non fornire informazioni riservate dell'azienda
- Mantieni le risposte brevi e concise per una conversazione telefonica naturale`,
    voice: 'Kore',
    language: 'it',
  },
  {
    id: 'booking',
    name: 'Prenotazioni',
    icon: '📅',
    prompt: `Sei un assistente per le prenotazioni di {{azienda}}. Aiuti i clienti a prenotare, modificare o cancellare appuntamenti.

Regole:
- Chiedi sempre data, ora e tipo di servizio desiderato
- Conferma i dettagli prima di procedere
- Se la data richiesta non è disponibile, suggerisci alternative
- Raccogli nome e recapito del cliente
- Invia un riepilogo della prenotazione alla fine`,
    voice: 'Leda',
    language: 'it',
  },
  {
    id: 'support',
    name: 'Supporto Tecnico',
    icon: '🔧',
    prompt: `Sei un agente di supporto tecnico per {{azienda}}. Aiuti i clienti a risolvere problemi tecnici con i prodotti e servizi.

Regole:
- Chiedi una descrizione chiara del problema
- Guida il cliente passo-passo nella risoluzione
- Se il problema richiede escalation, raccogli tutti i dettagli necessari
- Mantieni un tono paziente e comprensivo
- Verifica sempre che il problema sia risolto prima di chiudere`,
    voice: 'Charon',
    language: 'it',
  },
  {
    id: 'sales',
    name: 'Vendite',
    icon: '💼',
    prompt: `Sei un consulente vendite per {{azienda}}. Il tuo obiettivo è comprendere le esigenze del cliente e proporre le soluzioni più adatte.

Regole:
- Ascolta attentamente le esigenze del cliente
- Presenta i vantaggi dei prodotti/servizi in modo chiaro
- Non essere troppo aggressivo, mantieni un approccio consultivo
- Offri di inviare materiale informativo se il cliente ha bisogno di tempo
- Chiudi la conversazione con un follow-up chiaro`,
    voice: 'Puck',
    language: 'it',
  },
];

interface FormData {
  name: string;
  description: string;
  model: string;
  voice: string;
  language: string;
  system_prompt: string;
  first_message: string;
  temperature: number;
  max_duration_seconds: number;
  channel_sip: boolean;
  channel_webrtc: boolean;
  webrtc_widget_config: WebrtcWidgetConfig;
  rag_enabled: boolean;
  rag_synthesis_mode: string;
  tools_config: Record<string, unknown>[];
  google_search_enabled: boolean;
  code_execution_enabled: boolean;
  affective_dialog: boolean;
  proactive_audio: boolean;
  pre_call_hooks: PreCallHooksConfig;
  transcription_enabled: boolean;
  sentiment_enabled: boolean;
  context_memory_enabled: boolean;
  semantic_memory_enabled: boolean;
  is_active: boolean;
  vad_config: VadConfig;
  flow_definition?: FlowDefinition | null;
}

const defaultForm: FormData = {
  name: '',
  description: '',
  model: 'gemini-2.5-flash-native-audio-latest',
  voice: 'Kore',
  language: 'it',
  system_prompt: '',
  first_message: '',
  temperature: 0.7,
  max_duration_seconds: 600,
  channel_sip: false,
  channel_webrtc: false,
  webrtc_widget_config: { color: '#4285f4', position: 'bottom-right', cta_text: 'Parla con noi', allowed_domains: [] },
  rag_enabled: false,
  rag_synthesis_mode: 'native',
  tools_config: [],
  google_search_enabled: true,
  code_execution_enabled: false,
  affective_dialog: true,
  proactive_audio: false,
  pre_call_hooks: {},
  transcription_enabled: true,
  sentiment_enabled: false,
  context_memory_enabled: false,
  semantic_memory_enabled: false,
  is_active: true,
  vad_config: {
    sip_start_sensitivity: 'START_SENSITIVITY_HIGH',
    sip_end_sensitivity: 'END_SENSITIVITY_LOW',
    sip_prefix_padding_ms: 200,
    sip_silence_duration_ms: 500,
    webrtc_start_sensitivity: 'START_SENSITIVITY_HIGH',
    webrtc_end_sensitivity: 'END_SENSITIVITY_HIGH',
    webrtc_prefix_padding_ms: 20,
    webrtc_silence_duration_ms: 100,
  },
};

const STEPS = [
  { label: 'Generale', icon: Bot },
  { label: 'Modello', icon: Brain },
  { label: 'Voce', icon: Volume2 },
  { label: 'Prompt', icon: MessageSquare },
  { label: 'Knowledge', icon: FileText },
  { label: 'Tools', icon: Wrench },
  { label: 'Hooks', icon: Zap },
  { label: 'Canale', icon: Globe },
  { label: 'Avanzate', icon: SlidersHorizontal },
];

export function GeminiVoiceAgentModal({ open, onOpenChange, agent, onSaved }: GeminiVoiceAgentModalProps) {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<FormData>(defaultForm);
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [embedCopied, setEmbedCopied] = useState(false);

  const isEditing = !!agent;

  // Task #1316 — guard cambio chat model con doc multimodali Gemini.
  const kbGuard = useKbModelChangeGuard();

  // Models loaded via centralized hook (Task #1066). Lift-and-shift only:
  // identical shape to previous local fetch, zero behavior change.
  const llmModelsQuery = useChatModelOptions('gemini-voice', { enabled: open });
  const geminiModels = useMemo(() => {
    const list = llmModelsQuery.data?.models;
    if (!list || list.length === 0) return FALLBACK_GEMINI_MODELS;
    const mapped = list
      .filter((m) => m.status !== 'deprecated')
      .map((m) => ({
        id: m.id,
        name: `${m.displayName || m.name || m.id}${m.isRecommended ? ' (Consigliato)' : ''}`,
        desc: m.description || '',
      }));
    return mapped.length > 0 ? mapped : FALLBACK_GEMINI_MODELS;
  }, [llmModelsQuery.data]);
  const modelsOffline = llmModelsQuery.data?.source === 'fallback' || llmModelsQuery.isError;

  useEffect(() => {
    if (open) {
      if (agent) {
        setForm({
          name: agent.name || '',
          description: agent.description || '',
          model: agent.model || defaultForm.model,
          voice: agent.voice || 'Kore',
          language: agent.language || 'it',
          system_prompt: agent.system_prompt || '',
          first_message: agent.first_message || '',
          temperature: agent.temperature ?? 0.7,
          max_duration_seconds: agent.max_duration_seconds ?? 600,
          channel_sip: agent.channel_sip ?? false,
          channel_webrtc: agent.channel_webrtc ?? false,
          webrtc_widget_config: agent.webrtc_widget_config || defaultForm.webrtc_widget_config,
          rag_enabled: agent.rag_enabled ?? false,
          rag_synthesis_mode: agent.rag_synthesis_mode || 'native',
          tools_config: agent.tools_config || [],
          google_search_enabled: agent.google_search_enabled ?? true,
          code_execution_enabled: agent.code_execution_enabled ?? false,
          affective_dialog: agent.affective_dialog ?? true,
          proactive_audio: agent.proactive_audio ?? false,
          pre_call_hooks: normalizePreCallHooks(agent.pre_call_hooks),
          flow_definition: agent.flow_definition || null,
          transcription_enabled: agent.transcription_enabled ?? true,
          sentiment_enabled: agent.sentiment_enabled ?? false,
          context_memory_enabled: agent.context_memory_enabled ?? false,
          semantic_memory_enabled: agent.semantic_memory_enabled ?? false,
          is_active: agent.is_active ?? true,
          vad_config: {
            sip_start_sensitivity: ['START_SENSITIVITY_HIGH', 'START_SENSITIVITY_LOW', 'START_SENSITIVITY_UNSPECIFIED'].includes(agent.vad_config?.sip_start_sensitivity ?? '') ? agent.vad_config!.sip_start_sensitivity : defaultForm.vad_config.sip_start_sensitivity,
            sip_end_sensitivity: ['END_SENSITIVITY_HIGH', 'END_SENSITIVITY_LOW', 'END_SENSITIVITY_UNSPECIFIED'].includes(agent.vad_config?.sip_end_sensitivity ?? '') ? agent.vad_config!.sip_end_sensitivity : defaultForm.vad_config.sip_end_sensitivity,
            sip_prefix_padding_ms: (agent.vad_config?.sip_prefix_padding_ms != null && agent.vad_config.sip_prefix_padding_ms >= 10) ? agent.vad_config.sip_prefix_padding_ms : defaultForm.vad_config.sip_prefix_padding_ms,
            sip_silence_duration_ms: (agent.vad_config?.sip_silence_duration_ms != null && agent.vad_config.sip_silence_duration_ms >= 50) ? agent.vad_config.sip_silence_duration_ms : defaultForm.vad_config.sip_silence_duration_ms,
            webrtc_start_sensitivity: ['START_SENSITIVITY_HIGH', 'START_SENSITIVITY_LOW', 'START_SENSITIVITY_UNSPECIFIED'].includes(agent.vad_config?.webrtc_start_sensitivity ?? '') ? agent.vad_config!.webrtc_start_sensitivity : defaultForm.vad_config.webrtc_start_sensitivity,
            webrtc_end_sensitivity: ['END_SENSITIVITY_HIGH', 'END_SENSITIVITY_LOW', 'END_SENSITIVITY_UNSPECIFIED'].includes(agent.vad_config?.webrtc_end_sensitivity ?? '') ? agent.vad_config!.webrtc_end_sensitivity : defaultForm.vad_config.webrtc_end_sensitivity,
            webrtc_prefix_padding_ms: (agent.vad_config?.webrtc_prefix_padding_ms != null && agent.vad_config.webrtc_prefix_padding_ms >= 10) ? agent.vad_config.webrtc_prefix_padding_ms : defaultForm.vad_config.webrtc_prefix_padding_ms,
            webrtc_silence_duration_ms: (agent.vad_config?.webrtc_silence_duration_ms != null && agent.vad_config.webrtc_silence_duration_ms >= 50) ? agent.vad_config.webrtc_silence_duration_ms : defaultForm.vad_config.webrtc_silence_duration_ms,
          },
        });
      } else {
        setForm({ ...defaultForm });
      }
      setCurrentStep(0);
    }
  }, [open, agent]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Inserisci un nome per l\'agente', variant: 'destructive' });
      return;
    }
    await kbGuard.runGuardedSave({
      agentId: isEditing ? agent?.id ?? null : null,
      newChatModel: form.model,
      performSave: (opts) => doHandleSave(opts),
    });
  };

  const doHandleSave = async (opts: KbGuardedPerformSaveArgs): Promise<void> => {
    void opts;
    setIsSaving(true);
    try {
      if (isEditing) {
        await apiClient.put(`/gemini-voice/agents/${agent.id}`, form);
      } else {
        await apiClient.post('/gemini-voice/agents', form);
      }
      toast({ title: isEditing ? 'Agente aggiornato' : 'Agente creato' });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      // Task #1435 — limite tenant agenti AI: modal globale già aperto.
      if (isTenantLimitHandled(error)) {
        onOpenChange(false);
        return;
      }
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const applyTemplate = (template: typeof PROMPT_TEMPLATES[0]) => {
    setForm(prev => ({
      ...prev,
      system_prompt: template.prompt,
      voice: template.voice,
      language: template.language,
    }));
    toast({ title: `Template "${template.name}" applicato` });
  };

  const handlePlayVoice = async (voiceId: string) => {
    if (playingVoice === voiceId) {
      audioRef.current?.pause();
      setPlayingVoice(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlayingVoice(voiceId);
    try {
      const voiceInfo = GEMINI_VOICES.find(v => v.id === voiceId);
      const sampleText = form.language === 'en' 
        ? `Hello! I'm your virtual assistant. How can I help you today?`
        : form.language === 'es' ? `¡Hola! Soy tu asistente virtual. ¿Cómo puedo ayudarte hoy?`
        : form.language === 'fr' ? `Bonjour! Je suis votre assistant virtuel. Comment puis-je vous aider?`
        : form.language === 'de' ? `Hallo! Ich bin Ihr virtueller Assistent. Wie kann ich Ihnen helfen?`
        : `Ciao! Sono ${voiceInfo?.name || 'il tuo assistente'}. Come posso aiutarti oggi?`;
      const response = await apiClient.post<{ audioUrl?: string }>('/gemini-voice/voice-preview', {
        voice: voiceId,
        text: sampleText,
        language: form.language || 'it',
      });
      // La preview può arrivare dentro l'envelope (data.audioUrl) o al top-level.
      const responseFlat = response as ApiResponse<{ audioUrl?: string }> & { audioUrl?: string };
      const audioUrl = responseFlat?.data?.audioUrl || responseFlat?.audioUrl;
      if (audioUrl) {
        const audio = new Audio(audioUrl);
        audio.onended = () => setPlayingVoice(null);
        audio.onerror = () => setPlayingVoice(null);
        audioRef.current = audio;
        await audio.play();
      } else {
        toast({ title: 'Preview non disponibile', description: 'Impossibile generare audio per questa voce', variant: 'destructive' });
        setPlayingVoice(null);
      }
    } catch (error) {
      toast({ title: 'Errore preview', description: getErrorMessage(error) || 'Impossibile riprodurre la voce', variant: 'destructive' });
      setPlayingVoice(null);
    }
  };

  const copyEmbedCode = () => {
    const code = `<script src="${getPublicBaseUrl()}/api/widgets/gemini-voice.js" data-agent-id="${agent?.id || 'AGENT_ID'}" data-color="${form.webrtc_widget_config.color}" data-position="${form.webrtc_widget_config.position}" data-cta-text="${form.webrtc_widget_config.cta_text}"></script>`;
    navigator.clipboard.writeText(code);
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  const sections: AgentSectionDef[] = STEPS.map((s, i) => ({
    id: `s${i}`,
    label: s.label,
    icon: <s.icon className="h-4 w-4" />,
  }));
  const activeSectionId = `s${currentStep}`;
  const handleSectionChange = (id: string) => {
    const idx = parseInt(id.replace('s', ''), 10);
    if (!Number.isNaN(idx)) setCurrentStep(idx);
  };

  return (
    <>
    <AgentConfigShell
      open={open && !showFlowBuilder}
      onOpenChange={onOpenChange}
      title={isEditing ? `Modifica: ${agent.name}` : 'Nuovo Agente Vocale Gemini'}
      subtitle={isEditing ? "Modifica la configurazione dell'agente vocale Gemini Live" : 'Configura un nuovo agente vocale con Google Gemini Live API'}
      icon={<Sparkles className="h-5 w-5" />}
      accentColor="blue"
      sections={sections}
      activeSectionId={activeSectionId}
      onSectionChange={handleSectionChange}
      onSave={handleSave}
      footerRight={
        <>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={isSaving || !form.name.trim()}>
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            {isEditing ? 'Salva Modifiche' : 'Crea Agente'}
          </Button>
        </>
      }
    >
        <div className="space-y-4">
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Nome Agente *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="es. Receptionist Virtuale"
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Descrizione</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Breve descrizione dell'agente..."
                  className="text-sm min-h-[60px]"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Primo Messaggio</Label>
                <Textarea
                  value={form.first_message}
                  onChange={e => setForm(prev => ({ ...prev, first_message: e.target.value }))}
                  placeholder="es. Buongiorno, come posso aiutarla?"
                  className="text-sm min-h-[60px]"
                  rows={2}
                />
                <p className="text-[10px] text-muted-foreground">Il messaggio che l'agente pronuncia all'inizio della conversazione</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Template Prompt</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PROMPT_TEMPLATES.map(t => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t)}
                      className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors text-left"
                    >
                      <span className="text-lg">{t.icon}</span>
                      <div>
                        <p className="text-xs font-medium">{t.name}</p>
                        <p className="text-[10px] text-muted-foreground">Voce: {t.voice} · {LANGUAGES.find(l => l.code === t.language)?.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-medium">Modello</Label>
                  {modelsOffline && (
                    <span className="text-[10px] px-1.5 py-0 rounded border border-amber-300 bg-amber-50 text-amber-700" title="La lista modelli proviene dal fallback locale: la chiamata API al provider non è andata a buon fine.">⚠️ Lista offline — verifica chiave API</span>
                  )}
                </div>
                <Select value={form.model} onValueChange={v => setForm(prev => ({ ...prev, model: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {geminiModels.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        <div>
                          <span className="font-medium">{m.name}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">{m.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Temperatura: {form.temperature}</Label>
                <input
                  type="range"
                  value={form.temperature}
                  onChange={e => setForm(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                  min={0} max={2} step={0.1}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>Preciso (0)</span>
                  <span>Bilanciato (0.7)</span>
                  <span>Creativo (2)</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Durata Max Conversazione</Label>
                <Select value={String(form.max_duration_seconds)} onValueChange={v => setForm(prev => ({ ...prev, max_duration_seconds: parseInt(v) }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="300">5 minuti</SelectItem>
                    <SelectItem value="600">10 minuti</SelectItem>
                    <SelectItem value="900">15 minuti</SelectItem>
                    <SelectItem value="1200">20 minuti</SelectItem>
                    <SelectItem value="1800">30 minuti</SelectItem>
                    <SelectItem value="3600">1 ora</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
                <CardContent className="p-3 space-y-3">
                  <h4 className="text-xs font-medium flex items-center gap-1.5">
                    <Settings className="h-3.5 w-3.5 text-blue-600" />
                    Impostazioni Avanzate
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Affective Dialog</p>
                        <p className="text-[10px] text-muted-foreground">Risposte emotivamente consapevoli</p>
                      </div>
                      <Switch checked={form.affective_dialog} onCheckedChange={v => setForm(prev => ({ ...prev, affective_dialog: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Proactive Audio</p>
                        <p className="text-[10px] text-muted-foreground">L'agente può parlare proattivamente</p>
                      </div>
                      <Switch checked={form.proactive_audio} onCheckedChange={v => setForm(prev => ({ ...prev, proactive_audio: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Trascrizione</p>
                        <p className="text-[10px] text-muted-foreground">Salva trascrizioni delle conversazioni</p>
                      </div>
                      <Switch checked={form.transcription_enabled} onCheckedChange={v => setForm(prev => ({ ...prev, transcription_enabled: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Analisi Sentiment</p>
                        <p className="text-[10px] text-muted-foreground">Analizza il sentiment della conversazione</p>
                      </div>
                      <Switch checked={form.sentiment_enabled} onCheckedChange={v => setForm(prev => ({ ...prev, sentiment_enabled: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Memoria Contesto</p>
                        <p className="text-[10px] text-muted-foreground">Ricorda informazioni durante la sessione corrente</p>
                      </div>
                      <Switch checked={form.context_memory_enabled} onCheckedChange={v => setForm(prev => ({ ...prev, context_memory_enabled: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Memoria Semantica</p>
                        <p className="text-[10px] text-muted-foreground">Ricorda fatti e preferenze tra sessioni diverse</p>
                      </div>
                      <Switch checked={form.semantic_memory_enabled} onCheckedChange={v => setForm(prev => ({ ...prev, semantic_memory_enabled: v }))} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Voce</Label>
                <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                  {GEMINI_VOICES.map(v => (
                    <button
                      key={v.id}
                      onClick={() => setForm(prev => ({ ...prev, voice: v.id }))}
                      className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors text-left ${
                        form.voice === v.id
                          ? 'border-blue-400 bg-blue-50/80 dark:bg-blue-900/30 ring-1 ring-blue-400'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        form.voice === v.id ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                      }`}>
                        <Volume2 className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium">{v.name}</span>
                          <Badge className="text-[9px] px-1 py-0 bg-gray-100 dark:bg-gray-800 text-gray-500 border-0">{v.gender}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground">{v.style}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); handlePlayVoice(v.id); }}
                      >
                        {playingVoice === v.id ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      </Button>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Lingua</Label>
                <Select value={form.language} onValueChange={v => setForm(prev => ({ ...prev, language: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">Gemini supporta 70+ lingue. La lingua influenza la pronuncia e il riconoscimento vocale.</p>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">System Prompt</Label>
                <Textarea
                  value={form.system_prompt}
                  onChange={e => setForm(prev => ({ ...prev, system_prompt: e.target.value }))}
                  placeholder="Descrivi il ruolo, la personalità e le istruzioni per l'agente..."
                  className="text-sm min-h-[250px] font-mono"
                  rows={12}
                />
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">{form.system_prompt.length} caratteri</p>
                  <div className="flex gap-1">
                    {PROMPT_TEMPLATES.map(t => (
                      <Button key={t.id} variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => applyTemplate(t)}>
                        {t.icon} {t.name}
                      </Button>
                    ))}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setShowFlowBuilder(true)}
                >
                  <GitBranch className="h-4 w-4 mr-1" />
                  Flow Builder
                </Button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700/50">
                <div>
                  <Label>Knowledge Base RAG</Label>
                  <p className="text-xs text-gray-400">Risposte basate sui tuoi documenti</p>
                </div>
                <Switch checked={form.rag_enabled} onCheckedChange={v => setForm(prev => ({ ...prev, rag_enabled: v }))} />
              </div>

              {form.rag_enabled && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700/50 ml-4 border-l-2 border-blue-300">
                  <div className="flex items-center gap-1.5">
                    <Label>Sintesi Nativa</Label>
                    <div className="relative group">
                      <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-64 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                        <p className="font-medium mb-1">ON: L'agente ragiona direttamente sui dati recuperati dalla Knowledge Base. Più veloce, più naturale, costo inferiore.</p>
                        <p>OFF: I dati vengono prima sintetizzati da un modello ausiliario (GPT-4o-mini) e poi passati all'agente. Più controllata, utile con modelli costosi.</p>
                      </div>
                    </div>
                  </div>
                  <Switch checked={form.rag_synthesis_mode === 'native'} onCheckedChange={v => setForm(prev => ({ ...prev, rag_synthesis_mode: v ? 'native' : 'pre_synthesized' }))} />
                </div>
              )}

              {form.rag_enabled && (
                isEditing && agent?.id ? (
                  <KnowledgeBaseManager agentId={agent.id} agentType="gemini-voice" />
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Salva prima l'agente per gestire la Knowledge Base</p>
                  </div>
                )
              )}
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <h4 className="text-xs font-medium flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5 text-blue-600" />
                Tool Nativi Google
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="text-xs font-medium">Google Search</p>
                      <p className="text-[10px] text-muted-foreground">Ricerca web in tempo reale durante la conversazione</p>
                    </div>
                  </div>
                  <Switch checked={form.google_search_enabled} onCheckedChange={v => setForm(prev => ({ ...prev, google_search_enabled: v }))} />
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 opacity-60">
                  <div className="flex items-center gap-2">
                    <Code className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs font-medium">Code Execution</p>
                      <p className="text-[10px] text-muted-foreground">Non disponibile nella Live Audio API</p>
                    </div>
                  </div>
                  <Switch checked={false} disabled />
                </div>
              </div>

              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                <CardContent className="p-3">
                  <p className="text-[10px] text-amber-700 dark:text-amber-400">
                    <Info className="h-3 w-3 inline mr-1" />
                    Nella modalità Live Audio, solo Google Search è disponibile come tool nativo. Code Execution e URL Context non sono supportati.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-4">
              <h4 className="text-xs font-medium flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-amber-600" />
                Pre-call Hooks
              </h4>
              <p className="text-[10px] text-muted-foreground">Azioni eseguite automaticamente prima di ogni conversazione per arricchire il contesto dell'agente.</p>

              <PreCallHooksSection
                hooks={form.pre_call_hooks}
                onChange={(hooks) => setForm(prev => ({ ...prev, pre_call_hooks: hooks }))}
                agentType="voice"
              />
            </div>
          )}

          {currentStep === 7 && (
            <div className="space-y-4">
              <h4 className="text-xs font-medium flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-blue-600" />
                Canali di Comunicazione
              </h4>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="text-xs font-medium">SIP</p>
                      <p className="text-[10px] text-muted-foreground">Ricevi chiamate telefoniche tramite SIP trunk</p>
                    </div>
                  </div>
                  <Switch checked={form.channel_sip} onCheckedChange={v => setForm(prev => ({ ...prev, channel_sip: v }))} />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-purple-500" />
                    <div>
                      <p className="text-xs font-medium">WebRTC Widget</p>
                      <p className="text-[10px] text-muted-foreground">Widget embeddabile per siti web</p>
                    </div>
                  </div>
                  <Switch checked={form.channel_webrtc} onCheckedChange={v => setForm(prev => ({ ...prev, channel_webrtc: v }))} />
                </div>

                {form.channel_webrtc && (
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardContent className="p-3 space-y-3">
                      <h5 className="text-xs font-medium">Personalizzazione Widget</h5>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px]">Colore</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={form.webrtc_widget_config.color}
                              onChange={e => setForm(prev => ({ ...prev, webrtc_widget_config: { ...prev.webrtc_widget_config, color: e.target.value } }))}
                              className="w-8 h-8 rounded cursor-pointer border-0"
                            />
                            <Input
                              value={form.webrtc_widget_config.color}
                              onChange={e => setForm(prev => ({ ...prev, webrtc_widget_config: { ...prev.webrtc_widget_config, color: e.target.value } }))}
                              className="h-8 text-xs flex-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">Posizione</Label>
                          <Select
                            value={form.webrtc_widget_config.position}
                            onValueChange={v => setForm(prev => ({ ...prev, webrtc_widget_config: { ...prev.webrtc_widget_config, position: v } }))}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bottom-right">Basso Destra</SelectItem>
                              <SelectItem value="bottom-left">Basso Sinistra</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-[10px]">Testo CTA</Label>
                        <Input
                          value={form.webrtc_widget_config.cta_text}
                          onChange={e => setForm(prev => ({ ...prev, webrtc_widget_config: { ...prev.webrtc_widget_config, cta_text: e.target.value } }))}
                          placeholder="Parla con noi"
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-[10px]">Domini Consentiti</Label>
                        <div className="flex gap-1">
                          <Input
                            value={newDomain}
                            onChange={e => setNewDomain(e.target.value)}
                            placeholder="es. example.com"
                            className="h-8 text-xs"
                            onKeyDown={e => {
                              if (e.key === 'Enter' && newDomain.trim()) {
                                setForm(prev => ({
                                  ...prev,
                                  webrtc_widget_config: {
                                    ...prev.webrtc_widget_config,
                                    allowed_domains: [...(prev.webrtc_widget_config.allowed_domains || []), newDomain.trim()]
                                  }
                                }));
                                setNewDomain('');
                              }
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => {
                              if (newDomain.trim()) {
                                setForm(prev => ({
                                  ...prev,
                                  webrtc_widget_config: {
                                    ...prev.webrtc_widget_config,
                                    allowed_domains: [...(prev.webrtc_widget_config.allowed_domains || []), newDomain.trim()]
                                  }
                                }));
                                setNewDomain('');
                              }
                            }}
                          >+</Button>
                        </div>
                        {form.webrtc_widget_config.allowed_domains?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {form.webrtc_widget_config.allowed_domains.map((d: string, i: number) => (
                              <Badge key={i} className="text-[10px] gap-1 cursor-pointer hover:bg-red-50" onClick={() => {
                                setForm(prev => ({
                                  ...prev,
                                  webrtc_widget_config: {
                                    ...prev.webrtc_widget_config,
                                    allowed_domains: prev.webrtc_widget_config.allowed_domains.filter((_: string, idx: number) => idx !== i)
                                  }
                                }));
                              }}>
                                {d} ×
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {isEditing && (
                        <div className="space-y-1">
                          <Label className="text-[10px]">Codice Embed</Label>
                          <div className="relative">
                            <pre className="text-[10px] bg-gray-900 text-green-400 p-2 rounded overflow-x-auto">
                              {`<script src="${getPublicBaseUrl()}/api/widgets/gemini-voice.js"\n  data-agent-id="${agent?.id}"\n  data-color="${form.webrtc_widget_config.color}"\n  data-position="${form.webrtc_widget_config.position}"\n  data-cta-text="${form.webrtc_widget_config.cta_text}">\n</script>`}
                            </pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6"
                              onClick={copyEmbedCode}
                            >
                              {embedCopied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {currentStep === 8 && (
            <div className="space-y-4">
              <h4 className="text-xs font-medium flex items-center gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5 text-indigo-600" />
                Impostazioni Avanzate
              </h4>

              <div className="space-y-3">
                <h4 className="text-xs font-medium flex items-center gap-1.5">
                  <Mic className="h-3.5 w-3.5 text-green-600" />
                  VAD — Voice Activity Detection
                </h4>
                <p className="text-[10px] text-muted-foreground">Controlla come Gemini rileva quando l'utente inizia e smette di parlare. Configurazione separata per SIP (telefono) e WebRTC (widget web).</p>

                <div className="grid grid-cols-2 gap-3">
                  <Card className="border-blue-200 dark:border-blue-800">
                    <CardContent className="p-3 space-y-3">
                      <h5 className="text-xs font-medium flex items-center gap-1.5">
                        <Phone className="h-3 w-3 text-blue-500" /> SIP
                      </h5>
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Quanto facilmente il sistema rileva che l'utente ha iniziato a parlare. Bassa = meno falsi positivi da rumore di fondo, Alta = risposta più rapida ma sensibile al rumore.">Sensibilità Inizio Parlato <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Select value={form.vad_config.sip_start_sensitivity} onValueChange={v => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, sip_start_sensitivity: v } }))}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="START_SENSITIVITY_LOW">Bassa</SelectItem>
                              <SelectItem value="START_SENSITIVITY_HIGH">Alta</SelectItem>
                              <SelectItem value="START_SENSITIVITY_UNSPECIFIED">Auto (default Google)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Quanto facilmente il sistema rileva che l'utente ha smesso di parlare. Bassa = aspetta più a lungo prima di considerare la frase finita, Alta = risponde più rapidamente.">Sensibilità Fine Parlato <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Select value={form.vad_config.sip_end_sensitivity} onValueChange={v => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, sip_end_sensitivity: v } }))}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="END_SENSITIVITY_LOW">Bassa</SelectItem>
                              <SelectItem value="END_SENSITIVITY_HIGH">Alta</SelectItem>
                              <SelectItem value="END_SENSITIVITY_UNSPECIFIED">Auto (default Google)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Millisecondi di audio da preservare prima del punto di inizio parlato rilevato. Valori più alti evitano di tagliare le prime sillabe ma aggiungono latenza.">Prefix Padding (ms) <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Input type="number" value={form.vad_config.sip_prefix_padding_ms} onChange={e => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, sip_prefix_padding_ms: parseInt(e.target.value) || 0 } }))} className="h-7 text-[10px]" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Millisecondi di silenzio necessari prima che il sistema consideri la frase completata. Valori alti (800-1200ms) per SIP compensano la latenza telefonica.">Durata Silenzio (ms) <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Input type="number" value={form.vad_config.sip_silence_duration_ms} onChange={e => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, sip_silence_duration_ms: parseInt(e.target.value) || 0 } }))} className="h-7 text-[10px]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardContent className="p-3 space-y-3">
                      <h5 className="text-xs font-medium flex items-center gap-1.5">
                        <Globe className="h-3 w-3 text-purple-500" /> WebRTC
                      </h5>
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Quanto facilmente il sistema rileva che l'utente ha iniziato a parlare. Media è il default consigliato per WebRTC.">Sensibilità Inizio Parlato <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Select value={form.vad_config.webrtc_start_sensitivity} onValueChange={v => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, webrtc_start_sensitivity: v } }))}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="START_SENSITIVITY_LOW">Bassa</SelectItem>
                              <SelectItem value="START_SENSITIVITY_HIGH">Alta</SelectItem>
                              <SelectItem value="START_SENSITIVITY_UNSPECIFIED">Auto (default Google)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Quanto facilmente il sistema rileva che l'utente ha smesso di parlare. Alta è il default per WebRTC — risposte rapide senza tagliare le frasi.">Sensibilità Fine Parlato <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Select value={form.vad_config.webrtc_end_sensitivity} onValueChange={v => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, webrtc_end_sensitivity: v } }))}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="END_SENSITIVITY_LOW">Bassa</SelectItem>
                              <SelectItem value="END_SENSITIVITY_HIGH">Alta</SelectItem>
                              <SelectItem value="END_SENSITIVITY_UNSPECIFIED">Auto (default Google)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Millisecondi di audio da preservare prima dell'inizio parlato. WebRTC ha latenza bassa, quindi valori bassi (10-50ms) sono sufficienti.">Prefix Padding (ms) <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Input type="number" value={form.vad_config.webrtc_prefix_padding_ms} onChange={e => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, webrtc_prefix_padding_ms: parseInt(e.target.value) || 0 } }))} className="h-7 text-[10px]" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] flex items-center gap-1" title="Millisecondi di silenzio prima di considerare la frase finita. WebRTC è a bassa latenza, valori bassi (200-400ms) per risposte reattive.">Durata Silenzio (ms) <Info className="h-2.5 w-2.5 text-muted-foreground" /></Label>
                          <Input type="number" value={form.vad_config.webrtc_silence_duration_ms} onChange={e => setForm(prev => ({ ...prev, vad_config: { ...prev.vad_config, webrtc_silence_duration_ms: parseInt(e.target.value) || 0 } }))} className="h-7 text-[10px]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 mt-3">
                  <CardContent className="p-2.5">
                    <p className="text-[10px] text-amber-700 dark:text-amber-400">
                      <Info className="h-3 w-3 inline mr-1" />
                      SIP: sensibilità bassa e silenzi lunghi per compensare la latenza telefonica. WebRTC: sensibilità media e silenzi brevi per risposte più reattive.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
    </AgentConfigShell>

    <KbModelChangeGuardDialog state={kbGuard.state} primaryButtonClassName="bg-blue-600 hover:bg-blue-700 text-white" />

    <VoiceAgentFlowBuilder
      isOpen={showFlowBuilder}
      onClose={() => setShowFlowBuilder(false)}
      agentType="gemini"
      currentPrompt={form.system_prompt}
      flowDefinition={form.flow_definition || null}
      agentName={form.name}
      agentId={agent?.id}
      onSave={(prompt, flowDef) => {
        setForm(prev => ({ ...prev, system_prompt: prompt, flow_definition: flowDef }));
        setShowFlowBuilder(false);
      }}
    />
    </>
  );
}
